<template>
  <div
    :class="['border rounded-lg p-4 max-h-screen overflow-y-auto transition-all', 
            isFocused ? 'ring-2 ring-indigo-500' : '']"
    @keydown="handleKeyDown"
  >
    <h4 class="font-semibold mb-2">
      History for:
      {{ criterion?.all ? 'All Criteria' : criterion?.criterion.name || 'None' }}
    </h4>

    <!-- Current & Published meta info (only if NOT showing all histories) -->
    <div
      v-if="criterion && !criterion.all"
      class="mb-2 text-gray-700 font-medium space-y-1"
    >
      <div v-if="criterion?.criterion?.type === 'countable'">
        Current value: {{ criterion.count_value ?? 0 }}<br>
        Published value: {{ criterion.published_value ?? 0 }}
      </div>

      <div v-else-if="criterion?.criterion?.type === 'boolean'">
        Currently fulfilled: {{ criterion.is_fulfilled ? 'True' : 'False' }}<br>
        Published value: {{ criterion.published_value ? 'True' : 'False' }}
      </div>

      <div v-else>
        Value:
        {{ criterion.count_value || criterion.is_fulfilled || criterion.text_value }}<br>
        Published value:
        {{ criterion.published_value }}
      </div>
    </div>

    <!-- History List -->
    <div
      v-if="criterion?.all
            ? allHistories.length
            : (criterion?.history?.length)"
    >
      <ul>
        <li
          v-for="(h, i) in (criterion?.all ? allHistories : criterion.history)"
          :key="h.id"
          :ref="i === historyIndexFromParent ? 'activeItem' : null"
          :class="[ 'mb-2 border-b pb-2 flex flex-col space-y-1 relative',
                    i === historyIndexFromParent ? 'bg-indigo-100' : '' ]"
        >
          <!-- Timestamp + TEAM TAG -->
          <div class="text-sm text-gray-600">
            {{ formatDate(h.changed_at) }}
            <span v-if="teamMode && teamName" class="font-bold text-indigo-600">[{{ teamName }}]</span>
          </div>

          <!-- Value details -->
          <div class="text-gray-800">

            <!-- Show criterion name if in ALL mode -->
            <div v-if="criterion?.all" class="font-semibold text-indigo-700">
              {{ h.criterion_name || 'Unknown Criterion' }}
            </div>

            <!-- Edit Mode -->
            <div v-if="editingHistoryId === h.id" class="space-y-2">
              <div>
                <label class="text-sm font-semibold">Count:</label>
                <input
                  ref="editInput"
                  type="number"
                  v-model.number="editValue.int_value"
                  class="border rounded px-2 py-1 w-20 ml-2"
                />
              </div>
              <div v-if="h.bool_value !== null">
                <label class="text-sm font-semibold">
                  <input
                    type="checkbox"
                    v-model="editValue.bool_value"
                    class="mr-2"
                  />
                  Fulfilled
                </label>
              </div>
              <div v-if="h.text_value !== undefined">
                <label class="text-sm font-semibold">Text:</label>
                <textarea
                  v-model="editValue.text_value"
                  class="border rounded px-2 py-1 w-full mt-1"
                  rows="2"
                ></textarea>
              </div>
            </div>

            <!-- Display Mode -->
            <div v-else>
              <span v-if="h.int_value !== null">Count: {{ h.int_value }}</span>
              <!-- Change value indicator -->
              <span v-if="h.change_value !== undefined && h.change_value !== null" class="ml-2 text-sm">
                <span v-if="h.change_value > 0" class="text-emerald-600 font-semibold">▲ +{{ h.change_value }}</span>
                <span v-else-if="h.change_value < 0" class="text-red-600 font-semibold">▼ {{ h.change_value }}</span>
              </span>
              <span v-if="h.bool_value !== null"> | Fulfilled: {{ h.bool_value }}</span>
              <span v-if="h.text_value"> | {{ h.text_value }}</span>
            </div>
          </div>

          <!-- Publish toggle + Swap + Remove button -->
          <div class="flex items-center justify-between text-sm w-full">
            <label class="flex items-center space-x-2">
              <input
                type="checkbox"
                :checked="h.published"
                @change="togglePublish(h)"
              />
              <span>Published</span>
            </label>

            <div class="flex items-center space-x-2">
              <!-- Edit Icon - only show if not published -->
              <button
                v-if="!h.published"
                @click="editingHistoryId === h.id ? saveEdit(h) : startEdit(h)"
                :class="editingHistoryId === h.id ? 'text-green-600 hover:text-green-800' : 'text-blue-600 hover:text-blue-800'"
                :title="editingHistoryId === h.id ? 'Save changes' : 'Edit entry'"
              >
                <CheckIcon v-if="editingHistoryId === h.id" class="h-5 w-5" />
                <PencilIcon v-else class="h-5 w-5" />
              </button>

              <!-- Cancel Edit Icon - only show when editing -->
              <button
                v-if="editingHistoryId === h.id"
                @click="cancelEdit"
                class="text-gray-600 hover:text-gray-800"
                title="Cancel edit"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>

              <!-- Swap Icon - only show if user has a team OR if in team mode AND not published -->
              <button
                v-if="canSwap(h)"
                @click="swapHistoryEntry(h)"
                class="text-indigo-600 hover:text-indigo-800"
                title="Swap history to user or team"
              >
                <ArrowsRightLeftIcon class="h-5 w-5" />
              </button>

              <!-- Delete Icon -->
              <button
                @click="removeHistoryEntry(h)"
                class="text-red-600 hover:text-red-800"
              >
                <TrashIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <p v-else class="text-gray-500">No history for this criterion.</p>

    <!-- Swap History Modal -->
    <SwapHistoryModal
      :isOpen="showSwapModal"
      :sessionId="sessionId"
      :currentMode="teamMode ? 'team' : 'user'"
      :currentUserId="userId"
      :currentTeamId="teamId"
      @confirm="handleSwapConfirm"
      @cancel="showSwapModal = false"
    />
  </div>
</template>

<script>
import {
  setPublishState,
  deleteUserHistoryEntry,
  swapUserHistoryToTeam,
  swapUserHistoryToUser,
  updateUserHistoryEntry
} from "@/api/criterias";

import {
  setTeamPublishState,
  deleteTeamHistoryEntry,
  swapTeamHistoryToUser,
  swapTeamHistoryToTeam,
  updateTeamHistoryEntry
} from "@/api/teamCriteria";
import { TrashIcon, ArrowsRightLeftIcon, PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { getUserSession } from "@/live-sessions/api/usersessions";
import SwapHistoryModal from "./SwapHistoryModal.vue";

export default {
  components: {
    TrashIcon,
    ArrowsRightLeftIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    SwapHistoryModal
  },
  props: {
    criterion: { type: Object, default: null },
    historyIndexFromParent: Number,
    isFocused: Boolean,
    teamMode: { type: Boolean, default: false },
    teamId: { type: Number, default: null },
    teamName: { type: String, default: '' },
    userId: { type: Number, default: null },
    sessionId: { type: Number, default: null },
    allHistories: { type: Array, default: () => [] }
  },
  data() {
    return {
      userTeamId: null,
      editingHistoryId: null,
      editValue: {
        int_value: null,
        bool_value: null,
        text_value: null
      },
      showSwapModal: false,
      currentSwapEntry: null
    };
  },
  methods: {
    handleKeyDown(event) {
      // Handle digit keys when in edit mode
      if (this.editingHistoryId !== null && /^[0-9]$/.test(event.key)) {
        const target = event.target;
        const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        
        if (isInputField) {
          // If already in an input/textarea, just stop propagation to parent
          event.stopPropagation();
        } else {
          // If not in an input, prevent default, stop propagation, and focus the count input
          event.preventDefault();
          event.stopPropagation();
          
          // Focus the count input
          const countInput = this.$refs.editInput;
          if (countInput) {
            if (Array.isArray(countInput)) {
              countInput[0]?.focus();
            } else {
              countInput.focus();
            }
          }
        }
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },

    async togglePublish(historyEntry) {
      try {
        if (this.teamMode && this.teamId) {
          await setTeamPublishState(historyEntry.id, !historyEntry.published);
        } else {
          await setPublishState(historyEntry.id, !historyEntry.published);
        }
        historyEntry.published = !historyEntry.published;
        this.$emit("updated");
      } catch (err) {
        console.error("Failed to toggle publish:", err);
      }
    },
    async removeHistoryEntry(entry) {
      try {
        if (this.teamMode && this.teamId) {
          await deleteTeamHistoryEntry(entry.id);
        }
        else {
          await deleteUserHistoryEntry(entry.id);
        }
        this.$emit("updated");
      } catch (err) {
        console.error("Failed to delete history:", err);
      }
    },
    swapHistoryEntry(entry) {
      // Store the entry and open the modal
      this.currentSwapEntry = entry;
      this.showSwapModal = true;
    },
    async handleSwapConfirm(selection) {
      if (!this.currentSwapEntry) return;
      
      try {
        // Use change_value if available, otherwise null (will use int_value)
        const changeValue = this.currentSwapEntry.change_value ?? null;
        
        if (this.teamMode && this.teamId) {
          // Swapping from team
          if (selection.mode === 'user' && selection.userId) {
            // Swap to user
            await swapTeamHistoryToUser(this.currentSwapEntry.id, selection.userId, changeValue);
          } else if (selection.mode === 'team' && selection.teamId) {
            // Swap to another team
            await swapTeamHistoryToTeam(this.currentSwapEntry.id, selection.teamId, changeValue);
          }
        } else {
          // Swapping from user
          if (selection.mode === 'team' && selection.teamId) {
            // Swap to team
            await swapUserHistoryToTeam(this.currentSwapEntry.id, selection.teamId, changeValue);
          } else if (selection.mode === 'user' && selection.userId) {
            // Swap to another user
            await swapUserHistoryToUser(this.currentSwapEntry.id, selection.userId, changeValue);
          }
        }
        
        this.showSwapModal = false;
        this.currentSwapEntry = null;
        this.$emit("updated");
      } catch (err) {
        console.error("Failed to swap history entry:", err);
        alert('Failed to swap history entry: ' + (err.response?.data?.detail || err.message));
      }
    },
    canSwap(historyEntry) {
      // Don't allow swap if entry is already published
      if (historyEntry && historyEntry.published) {
        return false;
      }
      
      // Always allow swap - the modal will show available options
      return true;
    },
    async fetchUserTeam() {
      if (!this.userId || !this.sessionId) {
        this.userTeamId = null;
        return;
      }
      
      try {
        const res = await getUserSession(this.userId, this.sessionId);
        this.userTeamId = res.data.team_id;
      } catch (err) {
        console.error("Failed to fetch user session:", err);
        this.userTeamId = null;
      }
    },
    async removeHistoryEntryByIndex(index) {
      if (!this.criterion?.history || index < 0 || index >= this.criterion.history.length) return;
      const entry = this.criterion.history[index];
      await this.removeHistoryEntry(entry);
    },
    async togglePublishByIndex(index) {
      if (!this.criterion?.history || index < 0 || index >= this.criterion.history.length) return;
      const entry = this.criterion.history[index];
      await this.togglePublish(entry);
    },
    scrollToActive() {
      this.$nextTick(() => {
        const el = this.$refs.activeItem;
        if (el && el[0]) {
          el[0].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    },
    startEdit(entry) {
      this.editingHistoryId = entry.id;
      this.editValue = {
        int_value: entry.int_value,
        bool_value: entry.bool_value,
        text_value: entry.text_value
      };
    },
    cancelEdit() {
      this.editingHistoryId = null;
      this.editValue = {
        int_value: null,
        bool_value: null,
        text_value: null
      };
    },
    async saveEdit(entry) {
      try {
        const data = {
          int_value: this.editValue.int_value,
          bool_value: this.editValue.bool_value,
          text_value: this.editValue.text_value
        };
        
        if (this.teamMode && this.teamId) {
          await updateTeamHistoryEntry(entry.id, data);
        } else {
          await updateUserHistoryEntry(entry.id, data);
        }
        
        this.editingHistoryId = null;
        this.$emit("updated");
      } catch (err) {
        console.error("Failed to update history entry:", err);
        alert("Failed to update history entry: " + err.message);
      }
    },
  },
  watch: {
    historyIndexFromParent() {
      this.scrollToActive();
    },
    userId() {
      this.fetchUserTeam();
    },
    sessionId() {
      this.fetchUserTeam();
    }
  },
  async mounted() {
    await this.fetchUserTeam();
  }
};
</script>
